import React, { useEffect, useState, useCallback } from "react";
import { api, errMsg } from "@/lib/api";
import { Card, CardContent } from "@/components/ui/card";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter,
} from "@/components/ui/dialog";
import { Loader2, Plus, Pencil, Trash2 } from "lucide-react";
import { toast } from "sonner";

export default function AdminGruppenPage() {
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(true);
  const [form, setForm] = useState(null);
  const [saving, setSaving] = useState(false);

  const load = useCallback(() => {
    api.get("/gruppen").then((res) => setRows(res.data)).catch(() => {}).finally(() => setLoading(false));
  }, []);

  useEffect(() => { load(); }, [load]);

  const speichern = async () => {
    setSaving(true);
    try {
      const payload = { name: form.name, beschreibung: form.beschreibung || "" };
      if (form.id) {
        await api.put(`/gruppen/${form.id}`, payload);
        toast.success("Gruppe aktualisiert.");
      } else {
        await api.post("/gruppen", payload);
        toast.success("Gruppe angelegt.");
      }
      setForm(null);
      load();
    } catch (err) {
      toast.error(errMsg(err));
    } finally {
      setSaving(false);
    }
  };

  const loeschen = async (r) => {
    try {
      await api.delete(`/gruppen/${r.id}`);
      toast.success("Gruppe gelöscht.");
      load();
    } catch (err) {
      toast.error(errMsg(err));
    }
  };

  return (
    <div className="space-y-5">
      <div className="flex flex-wrap items-center justify-between gap-3">
        <div>
          <h1 className="text-2xl font-semibold">Lieferantengruppen</h1>
          <p className="text-sm text-muted-foreground">Gruppen für Touren, Regionen oder Beratungssegmente.</p>
        </div>
        <Button data-testid="group-create-button" onClick={() => setForm({ name: "", beschreibung: "" })}><Plus className="h-4 w-4 mr-2" /> Neue Gruppe</Button>
      </div>

      <Card>
        <CardContent className="p-0">
          {loading ? (
            <div className="flex justify-center py-16"><Loader2 className="h-6 w-6 animate-spin text-primary" /></div>
          ) : (
            <Table data-testid="groups-table">
              <TableHeader>
                <TableRow><TableHead>Name</TableHead><TableHead>Beschreibung</TableHead><TableHead className="text-right">Aktionen</TableHead></TableRow>
              </TableHeader>
              <TableBody>
                {rows.map((r) => (
                  <TableRow key={r.id}>
                    <TableCell className="font-medium">{r.name}</TableCell>
                    <TableCell className="text-sm text-muted-foreground">{r.beschreibung}</TableCell>
                    <TableCell className="text-right">
                      <div className="flex justify-end gap-1">
                        <Button data-testid={`group-edit-button-${r.name}`} variant="ghost" size="icon" onClick={() => setForm({ ...r })}><Pencil className="h-4 w-4" /></Button>
                        <Button data-testid={`group-delete-button-${r.name}`} variant="ghost" size="icon" onClick={() => loeschen(r)}><Trash2 className="h-4 w-4 text-destructive" /></Button>
                      </div>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          )}
        </CardContent>
      </Card>

      <Dialog open={!!form} onOpenChange={(o) => !o && setForm(null)}>
        <DialogContent data-testid="group-form-dialog" className="max-w-md">
          <DialogHeader><DialogTitle>{form?.id ? "Gruppe bearbeiten" : "Neue Gruppe"}</DialogTitle></DialogHeader>
          {form && (
            <div className="space-y-3">
              <div className="space-y-1.5">
                <Label>Name *</Label>
                <Input data-testid="group-form-name" value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} />
              </div>
              <div className="space-y-1.5">
                <Label>Beschreibung</Label>
                <Textarea value={form.beschreibung} onChange={(e) => setForm({ ...form, beschreibung: e.target.value })} rows={2} />
              </div>
            </div>
          )}
          <DialogFooter>
            <Button variant="outline" onClick={() => setForm(null)}>Abbrechen</Button>
            <Button data-testid="group-form-save-button" onClick={speichern} disabled={saving || !form?.name}>
              {saving && <Loader2 className="h-4 w-4 mr-2 animate-spin" />} Speichern
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
